import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box as MuiBox, Typography as MuiTypography, Tabs as MuiTabs, Tab as MuiTab, CircularProgress as MuiCircularProgress, Button as MuiButton, Grid as MuiGrid } from '@mui/material';
import { WidgetContainer } from '@/components/dashboard/WidgetContainer';
import { ErrorState } from '@/components/feedback/ErrorState';
import { usePageMeta } from '@/hooks/usePageMeta';
import { tokens } from '@/theme/tokens';
import { useSchoolDetails, useSchoolSummary } from '@/hooks/useSchools';
import { SchoolProfileHeader } from './components/SchoolProfileHeader';
import { ArrowLeft, Users, CheckCircle, IndianRupee, Clock, TrendingUp } from 'lucide-react';

function StatCard({ title, value, icon, color = 'primary', subtitle }) {
  return (
    <WidgetContainer sx={{ p: 3 }}>
      <MuiBox sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <MuiBox 
          sx={{ 
            width: 48, 
            height: 48, 
            borderRadius: 2, 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            bgcolor: tokens.colors[color]?.[50] || tokens.colors.primary[50],
            color: tokens.colors[color]?.main || tokens.colors.primary.main
          }}
        >
          {icon}
        </MuiBox>
        <MuiBox>
          <MuiTypography variant="body2" color="text.secondary" fontWeight={500}>
            {title}
          </MuiTypography>
          <MuiTypography variant="h5" fontWeight={700} color="text.primary">
            {value}
          </MuiTypography>
          {subtitle && (
            <MuiTypography variant="caption" color="text.muted">
              {subtitle}
            </MuiTypography>
          )}
        </MuiBox>
      </MuiBox>
    </WidgetContainer>
  );
}

export function SchoolDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { school, isLoading: isSchoolLoading, error } = useSchoolDetails(id);
  const { summary, isLoading: isSummaryLoading } = useSchoolSummary(id);
  const [activeTab, setActiveTab] = useState(0);

  usePageMeta(school ? school.name : 'School Details', [
    { label: 'Schools', path: '/schools' },
    { label: school ? school.name : 'Details' },
  ]);

  if (isSchoolLoading) {
    return (
      <MuiBox sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <MuiCircularProgress />
      </MuiBox>
    );
  }

  if (error) {
    return (
      <MuiBox sx={{ p: 3 }}>
        <ErrorState title="Failed to Load School" message={error} onRetry={() => window.location.reload()} />
      </MuiBox>
    );
  }

  if (!school) {
    return (
      <MuiBox sx={{ p: 3, textAlign: 'center' }}>
        <MuiTypography variant="h6">School not found.</MuiTypography>
        <MuiButton onClick={() => navigate('/schools')} sx={{ mt: 2 }}>Go Back</MuiButton>
      </MuiBox>
    );
  }

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <MuiBox sx={{ pb: 6 }}>
      <MuiBox sx={{ mb: 2 }}>
        <MuiButton 
          startIcon={<ArrowLeft size={18} />} 
          onClick={() => navigate('/schools')}
          color="inherit"
          sx={{ textTransform: 'none' }}
        >
          Back to Schools
        </MuiButton>
      </MuiBox>

      <SchoolProfileHeader school={school} onEdit={() => alert('Mock edit school')} />

      <WidgetContainer noPadding sx={{ mb: 3 }}>
        <MuiTabs 
          value={activeTab} 
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ 
            px: 2,
            borderBottom: 1, 
            borderColor: 'divider',
            '& .MuiTab-root': { minHeight: 64, fontWeight: 600 }
          }}
        >
          <MuiTab label="Overview" />
          <MuiTab label="Dashboard Summary" />
          <MuiTab label="Students" />
          <MuiTab label="Fees" />
          <MuiTab label="Attendance" />
          <MuiTab label="Documents" />
          <MuiTab label="Staff" />
          <MuiTab label="Settings" />
        </MuiTabs>
      </WidgetContainer>

      {activeTab === 0 && (
        <MuiBox sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <WidgetContainer title="Contact Information">
            <MuiGrid container spacing={2}>
              <MuiGrid size={{ xs: 12, sm: 6, md: 3 }}>
                <MuiTypography variant="body2" color="text.secondary">Phone</MuiTypography>
                <MuiTypography fontWeight={500}>{school.phone}</MuiTypography>
              </MuiGrid>
              <MuiGrid size={{ xs: 12, sm: 6, md: 3 }}>
                <MuiTypography variant="body2" color="text.secondary">Alternate Phone</MuiTypography>
                <MuiTypography fontWeight={500}>{school.alternatePhone}</MuiTypography>
              </MuiGrid>
              <MuiGrid size={{ xs: 12, sm: 6, md: 3 }}>
                <MuiTypography variant="body2" color="text.secondary">Email</MuiTypography>
                <MuiTypography fontWeight={500}>{school.email}</MuiTypography>
              </MuiGrid>
              <MuiGrid size={{ xs: 12, sm: 6, md: 3 }}>
                <MuiTypography variant="body2" color="text.secondary">Website</MuiTypography>
                <MuiTypography fontWeight={500}>{school.website}</MuiTypography>
              </MuiGrid>
              <MuiGrid size={{ xs: 12 }}>
                <MuiTypography variant="body2" color="text.secondary">Address</MuiTypography>
                <MuiTypography fontWeight={500}>{school.address}, {school.city}, {school.state}, {school.country} - {school.pinCode}</MuiTypography>
              </MuiGrid>
            </MuiGrid>
          </WidgetContainer>

          <WidgetContainer title="Administration">
            <MuiGrid container spacing={2}>
              <MuiGrid size={{ xs: 12, sm: 6, md: 3 }}>
                <MuiTypography variant="body2" color="text.secondary">Principal</MuiTypography>
                <MuiTypography fontWeight={500}>{school.principalName}</MuiTypography>
              </MuiGrid>
              <MuiGrid size={{ xs: 12, sm: 6, md: 3 }}>
                <MuiTypography variant="body2" color="text.secondary">Vice Principal</MuiTypography>
                <MuiTypography fontWeight={500}>{school.vicePrincipal}</MuiTypography>
              </MuiGrid>
              <MuiGrid size={{ xs: 12, sm: 6, md: 3 }}>
                <MuiTypography variant="body2" color="text.secondary">Office Contact</MuiTypography>
                <MuiTypography fontWeight={500}>{school.officeContact}</MuiTypography>
              </MuiGrid>
              <MuiGrid size={{ xs: 12, sm: 6, md: 3 }}>
                <MuiTypography variant="body2" color="text.secondary">Office Email</MuiTypography>
                <MuiTypography fontWeight={500}>{school.officeEmail}</MuiTypography>
              </MuiGrid>
            </MuiGrid>
          </WidgetContainer>

          <WidgetContainer title="Academic Information">
            <MuiGrid container spacing={4}>
              <MuiGrid size={{ xs: 6, sm: 3 }}>
                <MuiTypography variant="body2" color="text.secondary">Session</MuiTypography>
                <MuiTypography fontWeight={500}>{school.academicSession}</MuiTypography>
              </MuiGrid>
              <MuiGrid size={{ xs: 6, sm: 3 }}>
                <MuiTypography variant="body2" color="text.secondary">Classes</MuiTypography>
                <MuiTypography fontWeight={500}>{school.totalClasses}</MuiTypography>
              </MuiGrid>
              <MuiGrid size={{ xs: 6, sm: 3 }}>
                <MuiTypography variant="body2" color="text.secondary">Sections</MuiTypography>
                <MuiTypography fontWeight={500}>{school.totalSections}</MuiTypography>
              </MuiGrid>
              <MuiGrid size={{ xs: 6, sm: 3 }}>
                <MuiTypography variant="body2" color="text.secondary">Total Students</MuiTypography>
                <MuiTypography fontWeight={500}>{school.studentCount}</MuiTypography>
              </MuiGrid>
              <MuiGrid size={{ xs: 6, sm: 3 }}>
                <MuiTypography variant="body2" color="text.secondary">Teachers</MuiTypography>
                <MuiTypography fontWeight={500}>{school.teachers}</MuiTypography>
              </MuiGrid>
              <MuiGrid size={{ xs: 6, sm: 3 }}>
                <MuiTypography variant="body2" color="text.secondary">Staff</MuiTypography>
                <MuiTypography fontWeight={500}>{school.staff}</MuiTypography>
              </MuiGrid>
            </MuiGrid>
          </WidgetContainer>
        </MuiBox>
      )}

      {activeTab === 1 && (
        <MuiBox>
          {isSummaryLoading ? (
            <MuiCircularProgress />
          ) : summary ? (
            <MuiBox 
              sx={{ 
                display: 'grid', 
                gridTemplateColumns: { 
                  xs: '1fr', 
                  sm: 'repeat(2, 1fr)', 
                  md: 'repeat(3, 1fr)',
                  lg: 'repeat(5, 1fr)'
                }, 
                gap: 3,
                width: '100%'
              }}
            >
              <StatCard 
                title="Total Students" 
                value={school.studentCount} 
                icon={<Users />} 
                color="primary"
              />
              <StatCard 
                title="Today's Attendance" 
                value={`${summary.attendancePercentage}%`} 
                icon={<CheckCircle />} 
                color="success"
                subtitle={`${summary.todaysPresent} Present`}
              />
              <StatCard 
                title="Monthly Collection" 
                value={`₹${summary.thisMonthCollection.toLocaleString()}`} 
                icon={<IndianRupee />} 
                color="info"
                subtitle={`${summary.collectionPercentage}% of total target`}
              />
              <StatCard 
                title="Pending Fees" 
                value={`₹${summary.pendingFees.toLocaleString()}`} 
                icon={<Clock />} 
                color="warning"
              />
              <StatCard 
                title="New Admissions" 
                value={summary.newAdmissions} 
                icon={<TrendingUp />} 
                color="secondary"
              />
            </MuiBox>
          ) : (
            <MuiTypography>No summary available.</MuiTypography>
          )}
        </MuiBox>
      )}

      {activeTab > 1 && (
        <WidgetContainer>
          <MuiBox sx={{ textAlign: 'center', py: 8 }}>
            <MuiTypography variant="h6" color="text.secondary">
              This module is not built for this mock yet.
            </MuiTypography>
            <MuiTypography variant="body2" color="text.muted" sx={{ mt: 1 }}>
              It serves as a placeholder for future feature expansion.
            </MuiTypography>
          </MuiBox>
        </WidgetContainer>
      )}
    </MuiBox>
  );
}
